import {
  useState,
  type ReactNode,
} from 'react';
import {
  Badge,
  Button,
  Input,
  Text,
  Tooltip,
  mergeClasses,
} from '@coreai/fluentui-react';
import {
  CheckmarkRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  DismissRegular,
  FilterRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useFiltersPanelStyles } from './FiltersPanel.styles';

/** Color tone for the status row's leading dot. */
export type FiltersPanelStatusTone =
  | 'succeeded'
  | 'failed'
  | 'running'
  | 'cancelled'
  | 'skipped';

export type FiltersPanelStatusOption<V extends string> = {
  value: V;
  label: string;
  tone: FiltersPanelStatusTone;
};

export type FiltersPanelSegmentedOption<V extends string> = {
  value: V;
  label: string;
};

export type FiltersPanelSearch = {
  /** Visible section label. Defaults to "Search". */
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
};

export type FiltersPanelStatus<V extends string> = {
  /** Visible section label. Defaults to "Status". */
  label?: string;
  options: ReadonlyArray<FiltersPanelStatusOption<V>>;
  selected: ReadonlyArray<V>;
  onToggle: (value: V) => void;
};

export type FiltersPanelSegmented<V extends string> = {
  /** Visible section label, required (e.g. "Version", "Time Range"). */
  label: string;
  options: ReadonlyArray<FiltersPanelSegmentedOption<V>>;
  selected: V;
  onChange: (value: V) => void;
  /** Optional row rendered under the segmented control (e.g. custom range inputs). */
  customRow?: ReactNode;
};

export type FiltersPanelProps<
  S extends string = string,
  V extends string = string,
  T extends string = string,
> = {
  /** Heading text. Defaults to "Filters". */
  title?: string;
  /** Whether the body is expanded on first render. Defaults to true. */
  defaultExpanded?: boolean;
  /** Count shown in the header badge; also gates the "Clear all" button. */
  activeFilterCount: number;
  /** Invoked when the user clicks "Clear all filters". */
  onClear: () => void;
  /** ID prefix used to associate labels with controls. */
  idPrefix?: string;
  search?: FiltersPanelSearch;
  status?: FiltersPanelStatus<S>;
  version?: FiltersPanelSegmented<V>;
  timeRange?: FiltersPanelSegmented<T>;
  /**
   * Optional row shown directly under the header when the panel is collapsed.
   * Used by surfaces that want to keep filter pills visible at a glance.
   */
  collapsedSummary?: ReactNode;
};

const TONE_TO_DOT_KEY: Record<
  FiltersPanelStatusTone,
  | 'statusDotSucceeded'
  | 'statusDotFailed'
  | 'statusDotRunning'
  | 'statusDotCancelled'
  | 'statusDotSkipped'
> = {
  succeeded: 'statusDotSucceeded',
  failed: 'statusDotFailed',
  running: 'statusDotRunning',
  cancelled: 'statusDotCancelled',
  skipped: 'statusDotSkipped',
};

/**
 * Reusable filter sidebar used by Runs and Trigger history. Every section
 * (search, status, version, time range) is opt-in via props; pass only the
 * ones the consumer actually supports.
 */
export const FiltersPanel = <
  S extends string = string,
  V extends string = string,
  T extends string = string,
>({
  title = 'Filters',
  defaultExpanded = true,
  activeFilterCount,
  onClear,
  idPrefix = 'filters-panel',
  search,
  status,
  version,
  timeRange,
  collapsedSummary,
}: FiltersPanelProps<S, V, T>) => {
  const styles = useFiltersPanelStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const hasActive = activeFilterCount > 0;
  const bodyId = `${idPrefix}-body`;

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={expanded}
        {...(expanded ? { 'aria-controls': bodyId } : {})}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={styles.headerLeft}>
          <span className={styles.headerTitle}>
            <FilterRegular className={styles.headerIcon} />
            <Text>{title}</Text>
          </span>
          {hasActive && (
            <Badge
              appearance="filled"
              color="brand"
              size="small"
              className={styles.headerBadge}
            >
              {activeFilterCount}
            </Badge>
          )}
        </span>
        {expanded ? (
          <ChevronUpRegular className={styles.headerChevron} />
        ) : (
          <ChevronDownRegular className={styles.headerChevron} />
        )}
      </button>

      {!expanded && collapsedSummary}

      {expanded && (
        <div id={bodyId} className={styles.body}>
          {search && (
            <div className={styles.group}>
              <label
                className={styles.groupLabel}
                htmlFor={`${idPrefix}-search`}
              >
                {search.label ?? 'Search'}
              </label>
              <Input
                id={`${idPrefix}-search`}
                className={styles.searchInput}
                size="small"
                value={search.value}
                onChange={(_, data) => search.onChange(data.value)}
                placeholder={search.placeholder}
                contentBefore={<SearchRegular />}
                {...(search.value
                  ? {
                      contentAfter: (
                        <Tooltip content="Clear" relationship="label">
                          <Button
                            appearance="transparent"
                            size="small"
                            icon={<DismissRegular />}
                            aria-label="Clear search"
                            onClick={() => search.onChange('')}
                          />
                        </Tooltip>
                      ),
                    }
                  : {})}
              />
            </div>
          )}

          {status && (
            <div className={styles.group}>
              <span className={styles.groupLabel}>
                {status.label ?? 'Status'}
              </span>
              <div className={styles.statusOptions} role="group" aria-label={status.label ?? 'Status'}>
                {status.options.map((opt) => {
                  const isOn = status.selected.includes(opt.value);
                  const dotClass = styles[TONE_TO_DOT_KEY[opt.tone]];
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      role="checkbox"
                      aria-checked={isOn}
                      className={mergeClasses(
                        styles.statusOption,
                        isOn && styles.statusOptionSelected
                      )}
                      onClick={() => status.onToggle(opt.value)}
                    >
                      <span
                        aria-hidden="true"
                        className={mergeClasses(
                          styles.statusCheckbox,
                          isOn && styles.statusCheckboxChecked
                        )}
                      >
                        {isOn && <CheckmarkRegular fontSize={12} />}
                      </span>
                      <span
                        aria-hidden="true"
                        className={mergeClasses(styles.statusDot, dotClass)}
                      />
                      <span className={styles.statusText}>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {version && (
            <SegmentedSection idPrefix={`${idPrefix}-version`} config={version} />
          )}

          {timeRange && (
            <SegmentedSection
              idPrefix={`${idPrefix}-time`}
              config={timeRange}
            />
          )}

          {hasActive && (
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              onClick={onClear}
              className={styles.clearAll}
            >
              Clear all filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

type SegmentedSectionProps<V extends string> = {
  idPrefix: string;
  config: FiltersPanelSegmented<V>;
};

const SegmentedSection = <V extends string>({
  idPrefix,
  config,
}: SegmentedSectionProps<V>) => {
  const styles = useFiltersPanelStyles();
  return (
    <div className={styles.group}>
      <span className={styles.groupLabel} id={`${idPrefix}-label`}>
        {config.label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={`${idPrefix}-label`}
        className={styles.segmentedControl}
      >
        {config.options.map((opt) => {
          const isActive = opt.value === config.selected;
          return (
            <button
              type="button"
              key={opt.value}
              role="radio"
              aria-checked={isActive}
              className={mergeClasses(
                styles.segment,
                isActive && styles.segmentActive
              )}
              onClick={() => config.onChange(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {config.customRow}
    </div>
  );
};
